// RadioCards.jsx
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkmark } from '@carbon/icons-react';
import DisabledContext from 'antd/lib/config-provider/DisabledContext';
import classNames from 'classnames';
import { Button } from 'antd';

/**
 * RadioCards component - Displays a list of selectable card options
 *
 * @param {Object} props
 * @param {Array} props.options - Array of option objects
 * @param {string|Array|null} props.value - Currently selected value(s)
 * @param {function} props.onChange - Callback when selection changes
 * @param {string} props.className - Additional class names for container
 * @param {Object} props.styles - Custom style overrides
 * @param {string} props.direction - Layout direction ('horizontal' or 'vertical')
 * @param {string} props.variant - Card variant ('standalone' or 'inline')
 * @param {string} props.radioType - Type of radio button ('radio' or 'icon')
 * @param {boolean} props.multiple - If true, allows multiple selection (checkboxes)
 * @param {boolean} props.allowSelectAll - If true (and multiple), shows "Select all" / "Clear" controls
 * @param {string} props.spacing - Size of the spacing between inside cards ('small' or 'middle')
 * @param {string} props.size - Size of the cards ('default' or 'small')
 */
const RadioCards = ({
  options = [],
  value = null,
  onChange,
  direction = 'vertical',
  variant = 'standalone',
  radioType = 'radio',
  className = '',
  multiple = false,
  allowSelectAll = false,
  spacing = 'middle',
  size = 'default',
  disabled = false,
  ...rest
}) => {
  const [hoveredOption, setHoveredOption] = useState(null);

  const handleChange = (optionValue) => {
    // Prevent changes when disabled
    if (isDisabled) return;

    if (multiple) {
      // Multi-select mode
      let newValue = Array.isArray(value) ? [...value] : [];
      if (newValue.includes(optionValue)) {
        newValue = newValue.filter((v) => v !== optionValue);
      } else {
        newValue.push(optionValue);
      }

      if (onChange) onChange(newValue);
    } else {
      // Single-select mode
      if (onChange) {
        onChange(optionValue);
      }
    }
  };

  const handleMouseEnter = (optionValue) => {
    // Prevent hover effects when disabled
    if (isDisabled) return;
    setHoveredOption(optionValue);
  };

  const handleMouseLeave = () => {
    // Prevent hover effects when disabled
    if (isDisabled) return;
    setHoveredOption(null);
  };

  const _isInline = variant === 'inline';
  const _itemsHaveDescriptions = options.some((option) => option.description);
  const _isSmall = size === 'small';

  // Default styles that can be overridden
  const defaultStyles = {
    container: `flex ${
      direction === 'horizontal'
        ? `flex-col md:flex-row flex-wrap ${_isInline ? '' : _isSmall ? 'gap-2' : 'gap-4'}`
        : `flex-col ${_isInline ? '' : _isSmall ? 'space-y-2' : 'space-y-4'}`
    } w-full`,
    card: `box-border border border-solid ${
      _isInline
        ? 'border-t-[transparent] border-l-[transparent] border-r-[transparent] border-b-gray-200 last:border-b-[transparent]'
        : _isSmall
        ? 'rounded-lg'
        : 'rounded-[1rem]'
    } ${_isSmall ? 'p-4' : 'p-8'} ${
      !_itemsHaveDescriptions || spacing === 'small' || _isSmall ? (_isSmall ? 'py-3' : 'py-4') : ''
    } transition-all duration-200 ${
      direction === 'horizontal'
        ? `w-full ${_isSmall ? 'md:w-[calc(50%-0.25rem)]' : 'md:w-[calc(50%-0.5rem)]'}`
        : 'w-full'
    }
        `,
    disabledCard: 'cursor-not-allowed bg-gray-50 border-gray-200',
    disabledSelectedCard: 'cursor-not-allowed bg-gray-300 bg-opacity-70 border-gray-400 border-opacity-60',
    enabledCard: 'cursor-pointer',
    selectedCard: `bg-[rgba(67,97,238,0.08)] border-[#4361ee] ${
      _isInline
        ? 'border-l-[transparent] border-r-[transparent] border-t-[#4361ee] border-b-[#4361ee] mt-[-1px] first:mt-0'
        : 'shadow-[0px_0px_0px_1px_#4361ee]'
    }`,
    hoveredCard: `border-[#96a9ff] ${
      _isInline
        ? 'border-[transparent] shadow-none'
        : _isSmall
        ? 'shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
        : 'shadow-[0_2px_4px_rgba(0,0,0,0.05)] translate-y-[-2px]'
    }`,
    defaultCard: 'bg-white border-gray-200 [.ant-card.primary-card_&]:border-[#c4cedf]',
    radioCircle: `${_isSmall ? 'w-5 h-5' : 'w-6 h-6'} rounded-full border border-solid ${
      _isSmall ? 'mr-3' : 'mr-4'
    } flex items-center justify-center flex-shrink-0`,
    selectedRadio: 'border-[#4361ee] bg-[#4361ee]',
    selectedRadioDisabled: 'border-gray-400 bg-gray-400',
    hoveredRadio: 'border-[#4654fc] border-2',
    defaultRadio: 'border-gray-300 bg-white [.ant-card.primary-card_&]:border-[#c4cedf]',
    radioDot: `${_isSmall ? 'w-1.5 h-1.5' : 'w-2 h-2'} rounded-full bg-white`,
    checkbox: `${_isSmall ? 'w-5 h-5' : 'w-6 h-6'} rounded border border-solid ${
      _isSmall ? 'mr-3' : 'mr-4'
    } flex items-center justify-center flex-shrink-0`,
    selectedCheckbox: 'border-[#4361ee] text-white bg-[#4361ee]',
    selectedCheckboxDisabled: 'border-gray-400 bg-gray-400',
    hoveredCheckbox: 'border-[#4654fc] border-2',
    defaultCheckbox: 'border-gray-300 bg-white [.ant-card.primary-card_&]:border-[#c4cedf]',
    radioIconCircle:
      'w-16 h-16 rounded-full bg-[#eef3ff] mr-4 flex items-center justify-center flex-shrink-0 transition-all duration-200',
    radioIconCircleSelected: '!bg-[#4361ee]',
    radioIconSpan:
      'text-[#4361ee] text-2xl leading-[1] [&_svg]:text-[#4361ee] [&_svg]:w-8 [&_svg]:h-8 transition-all duration-200',
    radioIconSpanSelected: 'text-white [&_svg]:text-white mb-[-2px]',
    title: `${
      _isSmall ? 'text-[15px] leading-[20px] font-[600]' : 'text-[17px] leading-[24px] font-[650]'
    } text-gray-800`,
    description: `text-gray-500 ${_isSmall ? 'mt-1 text-sm' : 'mt-2'}`,
    hint: `${_isSmall ? 'mt-0.5' : 'mt-1'} text-[#2d31e3] font-medium ${_isSmall ? 'text-sm' : ''}`,
  };

  // Disabled state
  const isFormDisabled = useContext(DisabledContext);
  const isDisabled = disabled || isFormDisabled;

  const showSelectAll = multiple && allowSelectAll && !isDisabled;
  const selectedCount = Array.isArray(value) ? value.length : 0;
  const hasSelection = selectedCount > 0;
  const allSelected = selectedCount === options.length;

  return (
    <>
      {showSelectAll && (
        <div className="flex items-center gap-3 mb-3">
          <Button
            type="link"
            size="small"
            disabled={allSelected}
            onClick={() => onChange?.(options.map((o) => o.value))}
            className="p-0 h-auto text-[#4361ee] disabled:text-gray-300"
          >
            Select all
          </Button>
          {hasSelection && (
            <>
              <span className="text-gray-300 select-none">|</span>
              <Button
                type="link"
                size="small"
                onClick={() => onChange?.([])}
                className="p-0 h-auto text-gray-500 hover:text-red-500"
              >
                Clear
              </Button>
            </>
          )}
        </div>
      )}
      <div className={`${defaultStyles.container} ${className}`} data-testid={rest?.['data-testid']} role="group">
        {options.map((option, index) => {
          // Determine card styling based on state
          const isSelected = multiple ? Array.isArray(value) && value.includes(option.value) : value === option.value;
          const isHovered = hoveredOption === option.value && !isDisabled;

          let cardStyle = defaultStyles.card;

          if (isDisabled) {
            if (isSelected) {
              cardStyle += ` ${defaultStyles.disabledSelectedCard}`;
            } else {
              cardStyle += ` ${defaultStyles.disabledCard}`;
            }
          } else {
            cardStyle += ` ${defaultStyles.enabledCard}`;
            if (isSelected) {
              cardStyle += ` ${defaultStyles.selectedCard}`;
            } else if (isHovered) {
              cardStyle += ` ${defaultStyles.hoveredCard}`;
            } else {
              cardStyle += ` ${defaultStyles.defaultCard}`;
            }
          }

          // Radio/checkbox styling
          let control;
          if (multiple) {
            // Checkbox
            const checkboxStyle = `${defaultStyles.checkbox} ${
              isDisabled
                ? isSelected
                  ? defaultStyles.selectedCheckboxDisabled
                  : 'bg-gray-100 border-gray-300'
                : isSelected
                ? defaultStyles.selectedCheckbox
                : isHovered
                ? defaultStyles.hoveredCheckbox
                : defaultStyles.defaultCheckbox
            }`;
            control = (
              <div className={checkboxStyle}>
                {isSelected && <Checkmark className={isDisabled ? 'text-white' : ''} size={_isSmall ? 16 : 20} />}
              </div>
            );
          } else if (radioType === 'radio') {
            // Radio
            const radioStyle = `${defaultStyles.radioCircle} ${
              isDisabled
                ? isSelected
                  ? defaultStyles.selectedRadioDisabled
                  : 'bg-gray-100 border-gray-300'
                : isSelected
                ? defaultStyles.selectedRadio
                : isHovered
                ? defaultStyles.hoveredRadio
                : defaultStyles.defaultRadio
            }`;
            control = <div className={radioStyle}>{isSelected && <div className={defaultStyles.radioDot}></div>}</div>;
          } else {
            // Icon radio
            const radioIconCircleStyle = `${defaultStyles.radioIconCircle} ${
              isDisabled
                ? isSelected
                  ? 'bg-gray-300'
                  : 'bg-gray-200'
                : isSelected
                ? defaultStyles.radioIconCircleSelected
                : ''
            }`;
            const radioIconSpanStyle = `${defaultStyles.radioIconSpan} ${
              isDisabled ? 'text-gray-400 [&_svg]:text-gray-400' : isSelected ? defaultStyles.radioIconSpanSelected : ''
            }`;
            control = (
              <div className={radioIconCircleStyle}>
                <span className={radioIconSpanStyle}>{option.icon}</span>
              </div>
            );
          }

          return (
            <div
              key={option.value}
              className={classNames(cardStyle, {
                selected: isSelected,
                disabled: isDisabled,
              })}
              onClick={() => handleChange(option.value)}
              onMouseEnter={() => handleMouseEnter(option.value)}
              onMouseLeave={handleMouseLeave}
              data-testid={option['data-testid'] || `radio-card-${index + 1}`}
              data-radio-card-value={option.value}
              role={multiple ? 'checkbox' : 'radio'}
              aria-checked={isSelected}
              aria-disabled={isDisabled}
              tabIndex={isDisabled ? -1 : 0}
            >
              <div className="flex items-center justify-between h-full">
                <div className="flex items-center gap-1">
                  {control}
                  <div>
                    <h3 className={`${defaultStyles.title} ${isDisabled ? 'text-gray-400' : ''}`}>{option.label}</h3>
                    {option.description && (
                      <p
                        className={`${defaultStyles.description} ${isDisabled ? 'text-gray-300' : ''} ${
                          isSelected ? 'text-[rgb(82_87_99)]' : ''
                        }`}
                      >
                        {option.description}
                      </p>
                    )}

                    {option.hint && (
                      <p className={`${defaultStyles.hint} ${isDisabled ? 'text-gray-400' : ''}`}>{option.hint}</p>
                    )}
                  </div>
                </div>

                {radioType !== 'icon' && option.icon && (
                  <div className={isDisabled ? 'text-gray-400 [&_svg]:text-gray-400' : ''}>{option.icon}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <input
        type={multiple ? 'checkbox' : 'radio'}
        id={rest?.['id']}
        className="sr-only"
        aria-hidden="true"
        aria-required={rest?.['aria-required']}
        tabIndex={-1}
      />
    </>
  );
};

RadioCards.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      icon: PropTypes.node,
    }),
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.bool),
  ]),
  onChange: PropTypes.func,
  className: PropTypes.string,
  styles: PropTypes.object,
  multiple: PropTypes.bool,
  allowSelectAll: PropTypes.bool,
  size: PropTypes.oneOf(['default', 'small']),
  disabled: PropTypes.bool,
};

export default RadioCards;
